import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { HelixLoader } from "@/components/ui/helix-loader";
import { TopBar } from "@/components/course/TopBar";
import { LearningGoals } from "@/components/course/LearningGoals";
import { PathLayout } from "@/components/course/PathLayout";
import { UnitCard } from "@/components/course/UnitCard";
import AnimatedList from "@/components/course/AnimatedList";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Lock, CheckCircle2, BookOpen } from "lucide-react";
import { calculateLevel, getProgressToNextLevel, getXpRemainingToNextLevel } from "@/utils/levelCalculations";
import MascotDisplay from "@/components/MascotDisplay";
import SEO from "@/components/SEO";

interface Lesson {
  id: string;
  slug: string;
  title: string;
  order_index: number;
  xp_reward: number;
  chapter?: string;
  chapter_id?: string;
  is_placeholder?: boolean;
}

interface Chapter {
  id: string;
  title: string;
  grade_level: number;
  order_index: number;
}

interface Course {
  id: string;
  slug: string;
  title: string;
  description: string;
  category: string;
  chapters?: Chapter[];
  lessons: Lesson[];
  lesson_count?: number;
}

interface UserProgress {
  lesson_id: string;
  status: string;
}

interface UserProfile {
  level: number;
  xp_points: number;
  xp_total: number;
  streak?: number;
  avatar_url?: string | null;
}

interface LearningActivity {
  todayXp: number;
  lessonsThisWeek: number;
}

interface LessonGroup {
  name: string;
  lessons: Lesson[];
  progress: {
    completed: number;
    total: number;
  };
  totalXP: number;
  gradeLevel?: number;
}

interface CoursePageProps {
  courseSlugProp?: string;
}

const getSubjectColor = (category?: string, title?: string) => {
  const cat = category?.toLowerCase() || '';
  const t = title?.toLowerCase() || '';
  
  if (t.includes('astrophysics') || cat.includes('astrophysics') || cat === 'astro') {
    return '#F5A623';
  }
  if (t.includes('astronomy') || cat.includes('astronomy')) {
    return '#3BBFB8';
  }
  if (t.includes('quantum') || cat.includes('quantum') || cat === 'quantum') {
    return '#7BC843';
  }
  if (t.includes('physics') || cat.includes('physics')) {
    return '#E8527A';
  }
  return '#7BC843'; // Default fallback
};

const CHAPTER_COLORS = [
  '#60A5FA', // Chapter 1 / Grade 6
  '#A78BFA', // Chapter 2 / Grade 7
  '#F472B6', // Chapter 3 / Grade 8
  '#FCD34D', // Chapter 4 / Grade 9
  '#34D399', // Chapter 5 / Grade 10
  '#FB923C', // Chapter 6 / Grade 11
  '#F87171', // Chapter 7 / Grade 12
  '#22D3EE', // Chapter 8 / University
];

const hexToRgbStr = (hex: string) => {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
};

const getLessonRowStyles = (accentColor: string, isCompleted?: boolean, isCurrent?: boolean) => {
  const rgbaStr = hexToRgbStr(accentColor);
  
  if (isCurrent) {
    return {
      background: `linear-gradient(135deg, rgba(${rgbaStr}, 0.15) 0%, #2A3954 100%)`,
      circleBg: accentColor,
      circleTextColor: '#FFFFFF',
      titleColor: '#FFFFFF',
      xpColor: '#94A3B8',
      iconColor: accentColor,
      boxShadow: `0 0 20px rgba(${rgbaStr}, 0.2)`,
      cursor: 'pointer',
      opacity: 1,
      rightIconFill: accentColor,
    };
  }
  
  if (isCompleted) {
    return {
      background: `linear-gradient(135deg, rgba(${rgbaStr}, 0.08) 0%, #1E293B 100%)`,
      circleBg: accentColor,
      circleTextColor: '#FFFFFF',
      titleColor: '#F8FAFC',
      xpColor: '#94A3B8',
      iconColor: accentColor,
      boxShadow: 'none',
      cursor: 'pointer',
      opacity: 1,
      rightIconFill: accentColor,
    };
  }
  
  // Grey'ed out (not completed and not current)
  return {
    background: '#131B2E',
    circleBg: '#334155',
    circleTextColor: '#94A3B8',
    titleColor: '#64748B',
    xpColor: '#475569',
    iconColor: '#475569',
    boxShadow: 'none',
    cursor: 'not-allowed',
    opacity: 0.6,
    rightIconFill: 'none',
  };
};

interface LessonRowProps {
  lesson: Lesson;
  lessonIndex: number;
  displayTitle: string;
  isCompleted: boolean;
  rowStyles: ReturnType<typeof getLessonRowStyles>;
}

function LessonRow({ lesson, lessonIndex, displayTitle, isCompleted, rowStyles }: LessonRowProps) {
  return (
    <>
      <div
        className="animated-lesson-row w-full flex items-center justify-between"
        style={{
          background: rowStyles.background,
          borderRadius: '12px',
          padding: '18px 22px',
          border: 'none',
          outline: 'none',
          boxShadow: rowStyles.boxShadow,
          cursor: rowStyles.cursor,
          opacity: rowStyles.opacity,
        }}
      >
        <div className="flex items-center gap-4 w-full">
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              backgroundColor: rowStyles.circleBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: rowStyles.circleTextColor,
              fontWeight: 'bold',
              fontSize: '17px',
              flexShrink: 0,
            }}
          >
            {isCompleted ? '✓' : lessonIndex + 1}
          </div>

          <div className="flex-1 min-w-0" style={{ textAlign: 'left' }}>
            <h4
              style={{
                color: rowStyles.titleColor,
                fontWeight: 600,
                fontSize: '17px',
                margin: 0,
                lineHeight: '1.4',
              }}
              className="truncate"
            >
              {displayTitle}
            </h4>
            <div
              style={{
                color: rowStyles.xpColor,
                fontSize: '14px',
                marginTop: '2px',
              }}
            >
              {lesson.xp_reward || 100} XP
            </div>
          </div>

          <div className="flex-shrink-0 flex items-center">
            <Play
              size={20}
              style={{
                color: rowStyles.iconColor,
                fill: rowStyles.rightIconFill,
              }}
            />
          </div>
        </div>
      </div>

      {lesson.is_placeholder && (
        <div className="mt-2 text-left text-xs text-yellow-200">
          <span className="rounded-full border border-yellow-500/60 bg-yellow-500/10 px-2 py-1">Coming Soon</span>
        </div>
      )}
    </>
  );
}


export default function CoursePage({ courseSlugProp }: CoursePageProps = {}) {
  const { courseSlug: paramSlug } = useParams();
  const courseSlug = courseSlugProp || paramSlug;
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  const [progress, setProgress] = useState<UserProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile>({
    level: 1,
    xp_points: 0,
    xp_total: 0,
    streak: 0,
    avatar_url: null,
  });
  const [learningActivity, setLearningActivity] = useState<LearningActivity | null>(null);

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUserId(session.user.id);
        await loadCourse();
        await loadProgress(session.user.id);
        await loadUserProfile(session.user.id);
        await loadLearningActivity(session.user.id);
      } else {
        await loadCourse();
      }
      setLoading(false);
    };
    init();
  }, [courseSlug]);

  useEffect(() => {
    const mainEl = document.querySelector('main');
    if (mainEl) {
      mainEl.style.scrollSnapType = 'y mandatory';
      mainEl.classList.add('scroll-smooth');
    }
    return () => {
      if (mainEl) {
        mainEl.style.scrollSnapType = '';
        mainEl.classList.remove('scroll-smooth');
      }
    };
  }, []);

  const loadCourse = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('courses', {
        body: { slug: courseSlug }
      });

      if (error) {
        console.error('Error loading course:', error);
        return;
      }

      if (!data || data.error) {
        console.error('Course not found or has error');
        return;
      }

      const courseWithLessons = {
        ...data,
        lessons: data.lessons || [],
      };

      setCourse(courseWithLessons);
    } catch (err) {
      console.error('Exception in loadCourse:', err);
    }
  };

  const loadProgress = async (uid: string) => {
    const { data } = await supabase
      .from('user_progress')
      .select('lesson_id, status')
      .eq('user_id', uid);

    if (data) {
      setProgress(data);
    }
  };

  const loadUserProfile = async (uid: string) => {
    try {
      const [userStatsResult, profileResult] = await Promise.all([
        supabase.from("user_stats").select("xp_total").eq("user_id", uid).single(),
        supabase.from("profiles").select("xp_points, level, streak_count, avatar_url").eq("user_id", uid).single(),
      ]);

      const xp_total = userStatsResult.data?.xp_total || 0;
      const xp_points = profileResult.data?.xp_points || 0;
      const calculatedLevel = calculateLevel(xp_points);
      const streak = profileResult.data?.streak_count || 0;
      const avatar_url = profileResult.data?.avatar_url || null;

      setUserProfile({
        level: calculatedLevel,
        xp_points,
        xp_total,
        streak,
        avatar_url,
      });
    } catch (error) {
      console.error("Error loading user profile:", error);
    }
  };

  const loadLearningActivity = async (uid: string) => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    const startOfWeek = new Date(startOfToday);
    const daysSinceMonday = (startOfWeek.getDay() + 6) % 7;
    startOfWeek.setDate(startOfWeek.getDate() - daysSinceMonday);

    const { data, error } = await supabase
      .from('activity_log')
      .select('created_at, xp_earned')
      .eq('user_id', uid)
      .eq('activity_type', 'lesson_completed')
      .gte('created_at', startOfWeek.toISOString());

    if (error) {
      console.error('Error loading learning goals:', error);
      setLearningActivity(null);
      return;
    }

    const todayStartTime = startOfToday.getTime();
    const rows = data ?? [];
    const todayXp = rows.reduce((total, row) => {
      if (!row.created_at || new Date(row.created_at).getTime() < todayStartTime) return total;
      return total + Number(row.xp_earned ?? 0);
    }, 0);

    setLearningActivity({
      todayXp,
      lessonsThisWeek: rows.length,
    });
  };

  const getLessonStatus = (lessonId: string) => {
    const lessonProgress = progress.find(p => p.lesson_id === lessonId);
    return lessonProgress?.status || 'not_started';
  };

  // Group lessons by chapter
  const groupedUnits = useMemo(() => {
    if (!course?.lessons) return [];

    const chapterFallback = (chapterVal: any): string => {
      if (chapterVal === undefined || chapterVal === null) return 'Introduction';
      if (typeof chapterVal === 'string') {
        const trimmed = chapterVal.trim();
        return trimmed === '' ? 'Introduction' : trimmed;
      }
      return 'Introduction';
    };

    let chaptersList: { id: string; title: string; order_index: number; grade_level?: number }[] = [];

    if (course.chapters && course.chapters.length > 0) {
      chaptersList = course.chapters.map(c => ({
        id: c.id,
        title: c.title,
        order_index: c.order_index ?? 0,
        grade_level: c.grade_level ?? 1
      })).sort((a, b) => a.order_index - b.order_index);
    } else {
      // Fallback: Build unique chapters from lessons
      const uniqueChapters = new Set<string>();
      course.lessons.forEach(l => {
        const chapter = chapterFallback(l.chapter);
        uniqueChapters.add(chapter);
      });

      const extractOrdinalNumber = (name: string): number => {
        if (!name || typeof name !== 'string') return 999;
        const match = name.match(/chapter\s+(\d+)/i) || name.match(/grade\s+(\d+)/i) || name.match(/unit\s+(\d+)/i) || name.match(/^(\d+)/);
        return match ? parseInt(match[1], 10) : 999;
      };

      const sortedChapterNames = Array.from(uniqueChapters).sort((a, b) => {
        const strA = typeof a === 'string' ? a : '';
        const strB = typeof b === 'string' ? b : '';
        const numA = extractOrdinalNumber(strA);
        const numB = extractOrdinalNumber(strB);
        if (numA !== numB) return numA - numB;
        return strA.localeCompare(strB);
      });

      chaptersList = sortedChapterNames.map((name, idx) => ({
        id: name,
        title: name,
        order_index: idx
      }));
    }

    // Now group lessons
    const groupsMap = new Map<string, Lesson[]>();
    chaptersList.forEach(c => {
      groupsMap.set(c.id, []);
    });

    course.lessons.forEach((lesson) => {
      let cid = lesson.chapter_id;
      if (course.chapters && course.chapters.length > 0) {
        if (!cid || !chaptersList.some(c => c.id === cid)) {
          cid = chaptersList[0]?.id || 'unknown';
        }
      } else {
        const chapter = chapterFallback(lesson.chapter);
        cid = chapter;
      }

      if (!groupsMap.has(cid)) {
        groupsMap.set(cid, []);
      }
      groupsMap.get(cid)!.push(lesson);
    });

    const units: LessonGroup[] = chaptersList.map(chapter => {
      const lessons = groupsMap.get(chapter.id) || [];
      const visibleLessons = lessons.filter(l => !l.is_placeholder);
      const progressLessons = visibleLessons.length ? visibleLessons : lessons;

      // Ensure lessons within a chapter are ordered
      progressLessons.sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));

      const completed = progressLessons.filter(l => {
        const lessonProgress = progress.find(p => p.lesson_id === l.id);
        return lessonProgress?.status === 'completed';
      }).length;

      const totalXP = progressLessons.reduce((sum, l) => sum + (l.xp_reward || 100), 0);

      return {
        name: chapter.title,
        lessons: progressLessons,
        progress: {
          completed,
          total: progressLessons.length
        },
        totalXP,
        gradeLevel: chapter.grade_level
      };
    });

    return units;
  }, [course?.lessons, course?.chapters, progress]);

// We can remove extractOrdinalNumber since sorting is now handled by the database order_index

  // Determine which unit is current (first incomplete)
  const currentUnitIndex = useMemo(() => {
    return groupedUnits.findIndex(unit => unit.progress.completed < unit.progress.total);
  }, [groupedUnits]);

  // Find the first incomplete lesson across the entire course to identify the active/current one
  const firstIncompleteLesson = useMemo(() => {
    if (!course?.lessons) return null;
    const sorted = [...course.lessons].sort((a, b) => a.order_index - b.order_index);
    return sorted.find(l => getLessonStatus(l.id) !== 'completed') || null;
  }, [course?.lessons, progress]);

  // No locking - always return false (chapters are never locked)
  const isChapterLocked = (chapterIndex: number) => {
    return false; // No chapter locking
  };

  // Lock if it's not completed AND not the current (first incomplete) one, AND not in_progress
  const isLessonLocked = (lesson: Lesson, chapterIndex?: number) => {
    if (lesson.is_placeholder) return true;
    
    const status = getLessonStatus(lesson.id);
    if (status === 'completed' || status === 'in_progress') {
      return false;
    }
    
    if (!firstIncompleteLesson) return false; // all completed, none locked
    
    const isCurrent = lesson.id === firstIncompleteLesson.id;
    return !isCurrent;
  };

  const handleLessonClick = async (lesson: Lesson) => {
    if (lesson.is_placeholder) {
      toast.info('This lesson is coming soon!');
    } else {
      // Navigate directly to the lesson - no blocking
      // Create an in_progress record if one doesn't exist or transition from not_started to in_progress
      const { data: existingProgress } = await supabase
        .from('user_progress')
        .select('status')
        .eq('user_id', userId)
        .eq('lesson_id', lesson.id)
        .maybeSingle();

      if (!existingProgress) {
        await supabase
          .from('user_progress')
          .insert({
            user_id: userId,
            lesson_id: lesson.id,
            status: 'in_progress',
          });
      } else if (existingProgress.status === 'not_started') {
        await supabase
          .from('user_progress')
          .update({
            status: 'in_progress',
          })
          .eq('user_id', userId)
          .eq('lesson_id', lesson.id);
      }
    }

    navigate(`/lesson/${lesson.slug}/1`);
  };

  const isUnitCompleted = (index: number) => {
    const unit = groupedUnits[index];
    if (!unit) return false;
    return unit.progress.completed >= unit.progress.total;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <HelixLoader className="text-primary" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-2">Course Not Found</h2>
          <p className="text-muted-foreground mb-4">The course you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/learning')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  const xpToNextLevel = getXpRemainingToNextLevel(userProfile.xp_points);
  const progressToNextLevel = getProgressToNextLevel(userProfile.xp_points);

  const hasGroupedLessons = groupedUnits.length > 0 && groupedUnits.some(u => u.lessons.length > 0);

  const totalXPAvailable = hasGroupedLessons
    ? groupedUnits.reduce((sum, u) => sum + u.totalXP, 0)
    : (course.lessons || []).reduce((sum, l) => sum + (l.xp_reward || 100), 0);

  const lessonsCompleted = hasGroupedLessons
    ? groupedUnits.reduce((sum, u) => sum + u.progress.completed, 0)
    : (course.lessons || []).filter(l => getLessonStatus(l.id) === 'completed').length;

  const seoTitle = courseSlug?.toLowerCase().includes("astrophysics")
    ? "Astrophysics Course"
    : "Physics Course";
  const seoDescription = courseSlug?.toLowerCase().includes("astrophysics")
    ? "Explore the universe across 8 grade levels — from our solar system to quantum astrophysics."
    : "Master classical and modern physics from Grade 6 through university level.";

  return (
    <div className="min-h-screen bg-background">
      <SEO title={seoTitle} description={seoDescription} />
      {/* Top Bar */}
      <TopBar
        courseTitle={course.title}
        xp={userProfile.xp_points}
        xpToNextLevel={xpToNextLevel}
        progressToNextLevel={progressToNextLevel}
        level={userProfile.level}
        streak={userProfile.streak}
        completedToday={Boolean(learningActivity?.todayXp)}
      />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6 max-w-4xl lg:max-w-6xl">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Column (62% width on desktop) */}
          <div className="w-full lg:w-[62%]">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <span>Studying {course.title}</span>
              <span>·</span>
              <button onClick={() => navigate('/learning?change=true')} className="hover:text-primary transition-colors">
                Change
              </button>
            </div>

            {/* Course Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 rounded-xl bg-gradient-to-r from-primary/10 to-purple-500/10 border border-primary/20"
            >
              <h1 className="text-xl font-bold mb-2">{course.title}</h1>
              <p className="text-muted-foreground">{course.description}</p>
            </motion.div>

            {learningActivity && (
              <div className="mb-6 lg:hidden">
                <LearningGoals
                  todayXp={learningActivity.todayXp}
                  lessonsThisWeek={learningActivity.lessonsThisWeek}
                  level={userProfile.level}
                  levelProgress={progressToNextLevel}
                  xpToNextLevel={xpToNextLevel}
                />
              </div>
            )}

            {/* Learning Path */}
            <PathLayout>
              <AnimatePresence mode="popLayout">
                {hasGroupedLessons ? (
                  groupedUnits.map((unit, unitIndex) => {
                    const chapterLocked = isChapterLocked(unitIndex);
                    const chapterCompleted = isUnitCompleted(unitIndex);
                    const chapterColor = CHAPTER_COLORS[unitIndex % CHAPTER_COLORS.length];

                    return (
                      <div
                        key={unit.name}
                        className="mb-8 w-full scroll-mt-20 md:scroll-mt-24"
                        style={{ scrollSnapAlign: 'start' }}
                      >
                        {/* Chapter Header */}
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mb-6"
                        >
                          <div
                            className={`p-4 rounded-xl border-2 transition-all ${
                              chapterLocked
                                ? 'bg-muted/30 border-muted opacity-50'
                                : chapterCompleted
                                ? 'bg-success/10 border-success/50'
                                : 'bg-primary/10 border-primary/50'
                            }`}
                            style={{
                              borderLeft: `4px solid ${chapterColor}`,
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                {unit.gradeLevel !== undefined && (
                                  <div className="mb-1 flex">
                                    <span 
                                      className="inline-block text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border"
                                      style={{
                                        borderColor: `${chapterColor}40`,
                                        color: chapterColor,
                                        backgroundColor: `${chapterColor}10`
                                      }}
                                    >
                                      {unit.gradeLevel === 13 ? 'University' : `Grade ${unit.gradeLevel}`}
                                    </span>
                                  </div>
                                )}
                                <h3
                                  className="text-lg font-bold flex items-center gap-2"
                                  style={{ color: chapterColor }}
                                >
                                  {chapterLocked && <Lock className="w-5 h-5" />}
                                  {unit.name}
                                </h3>
                                <p className="text-sm text-muted-foreground">
                                  {unit.progress.completed} / {unit.progress.total} lessons completed
                                </p>
                              </div>
                              {chapterCompleted && (
                                <CheckCircle2 className="w-8 h-8 text-success" />
                              )}
                            </div>
                            {!chapterLocked && (
                              <div className="mt-2">
                                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                                  <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${unit.progress.total > 0 ? (unit.progress.completed / unit.progress.total) * 100 : 0}%` }}
                                    transition={{ duration: 0.6 }}
                                    className={chapterCompleted ? 'h-full bg-success' : 'h-full bg-gradient-to-r from-primary to-purple-600'}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>

                        {/* Lessons in this chapter */}
                        <AnimatedList
                          items={unit.lessons}
                          getItemKey={(lesson) => lesson.id}
                          isItemDisabled={(lesson) => isLessonLocked(lesson, unitIndex)}
                          initialSelectedIndex={unit.lessons.findIndex((lesson) => lesson.id === firstIncompleteLesson?.id)}
                          onItemSelect={(lesson) => void handleLessonClick(lesson)}
                          ariaLabel={`${unit.name} lessons`}
                          renderItem={(lesson, lessonIndex) => {
                            const isLessonCompleted = getLessonStatus(lesson.id) === 'completed';
                            const isLessonCurrent = lesson.id === firstIncompleteLesson?.id;
                            const displayTitle = lesson.title && lesson.title !== unit.name
                              ? lesson.title
                              : `${unit.name} - Lesson ${lessonIndex + 1}`;

                            return (
                              <LessonRow
                                lesson={lesson}
                                lessonIndex={lessonIndex}
                                displayTitle={displayTitle}
                                isCompleted={isLessonCompleted}
                                rowStyles={getLessonRowStyles(chapterColor, isLessonCompleted, isLessonCurrent)}
                              />
                            );
                          }}
                        />
                      </div>
                    );
                  })
                ) : (
                  // Fallback: Render all lessons in a single flat list without chapter grouping
                  <AnimatedList
                    items={course.lessons || []}
                    getItemKey={(lesson) => lesson.id}
                    isItemDisabled={(lesson) => isLessonLocked(lesson)}
                    initialSelectedIndex={(course.lessons || []).findIndex((lesson) => lesson.id === firstIncompleteLesson?.id)}
                    onItemSelect={(lesson) => void handleLessonClick(lesson)}
                    ariaLabel={`${course.title} lessons`}
                    className="max-w-xl"
                    renderItem={(lesson, lessonIndex) => {
                      const isLessonCompleted = getLessonStatus(lesson.id) === 'completed';
                      const isLessonCurrent = lesson.id === firstIncompleteLesson?.id;

                      return (
                        <LessonRow
                          lesson={lesson}
                          lessonIndex={lessonIndex}
                          displayTitle={lesson.title || `Lesson ${lessonIndex + 1}`}
                          isCompleted={isLessonCompleted}
                          rowStyles={getLessonRowStyles('#60A5FA', isLessonCompleted, isLessonCurrent)}
                        />
                      );
                    }}
                  />
                )}
              </AnimatePresence>
            </PathLayout>

            {/* Course Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4"
            >
              <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-600/10 border border-blue-500/20">
                <div className="text-2xl font-bold text-blue-400">{course.lessons?.length || 0}</div>
                <div className="text-sm text-muted-foreground">Total Lessons</div>
              </div>
              <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-purple-600/10 border border-purple-500/20">
                <div className="text-2xl font-bold text-purple-400">
                  {totalXPAvailable}
                </div>
                <div className="text-sm text-muted-foreground">Total XP Available</div>
              </div>
              <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/10 to-green-600/10 border border-green-500/20">
                <div className="text-2xl font-bold text-green-400">
                  {lessonsCompleted}
                </div>
                <div className="text-sm text-muted-foreground">Lessons Completed</div>
              </div>
            </motion.div>
          </div>

          {/* Right Column (38% width on desktop, hidden on mobile) */}
          <div className="hidden lg:block lg:w-[38%]">
            <div
              id="mascot-zone"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '360px',
              }}
            >
              <MascotDisplay state="idle" size="lg" />
            </div>
            {learningActivity && (
              <LearningGoals
                todayXp={learningActivity.todayXp}
                lessonsThisWeek={learningActivity.lessonsThisWeek}
                level={userProfile.level}
                levelProgress={progressToNextLevel}
                xpToNextLevel={xpToNextLevel}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
